/**
 * The department filter, once, for every screen that has one.
 *
 * `GET /employees/departments` has existed since Task T2-A and `PeopleScreen` has
 * been using it as a working `<select>` since T2-E-web. Four other screens went on
 * asking a person to type an internal id with "danh sách chọn sẽ có sau" printed
 * underneath — a promise on screen instead of a control, and the finish review's
 * fourth finding. This is that select, extracted: same endpoint, same facet rows,
 * same "Tất cả phòng ban" first option.
 *
 * ── What it deliberately does not offer ─────────────────────────────────────
 *
 * The facet with `department_id: null` — employees who have no department — is left
 * off the list, for `PeopleScreen`'s reason: `department_id` is an exact match on a
 * real id, so an option for "no department" would collide with "all departments" on
 * the empty-string value and then silently do nothing when picked.
 *
 * ── A refusal is not an empty company ───────────────────────────────────────
 *
 * If the read fails the control still renders, with the one option it can be sure
 * of, and says in one line that the list could not be read. Blanking the filter
 * would tell a manager their company has no departments.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { employeeDepartmentsPath, type EmployeeDepartmentFacetList } from '../api/employees';
import { useApiQuery } from '../api/useApiQuery';
import { Icon } from '../states/icons';

export interface DepartmentSelectProps {
  /** The selected department id, or `null` for every department in scope. */
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  readonly label: string;
  /** DOM id of the `<select>`; the label points at it. */
  readonly id: string;
  readonly testId?: string;
}

export function DepartmentSelect({
  value,
  onChange,
  label,
  id,
  testId = 'department-select',
}: DepartmentSelectProps): ReactNode {
  const { t } = useI18n();
  const departments = useApiQuery<EmployeeDepartmentFacetList>(employeeDepartmentsPath());
  const facets = (departments.data?.departments ?? []).filter(
    (facet) => facet.department_id !== null,
  );

  return (
    <div className="wp-field" data-testid={testId}>
      <label className="wp-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="wp-select">
        <select
          id={id}
          className="wp-field__input"
          value={value ?? ''}
          data-testid={`${testId}-input`}
          onChange={(event) => {
            const next = event.target.value;
            onChange(next === '' ? null : next);
          }}
        >
          <option value="">{t('department.all')}</option>
          {facets.map((facet) => (
            <option key={facet.department_id} value={facet.department_id ?? ''}>
              {facet.department_name ?? t('people.noDepartment')}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" className="wp-select__chevron" />
      </div>
      {departments.status === 'error' ? (
        <p className="wp-small wp-muted" data-testid={`${testId}-unreadable`}>
          {t('department.unreadable')}
        </p>
      ) : null}
    </div>
  );
}
